<template>
	<div class="grid-flex-container">
		<div class="grid-toolbar">
			<quick-filter
				:Columns="filterFields"
				:filter="quickFilter"
				@filterChanged="onQuickFilterChanged"
			/>
			<filters-dialog
				v-if="false"
				:columns="filterFields"
				:filter="advancedFilter"
				@filterChanged="onAdvancedFilterChanged"
			/>
		</div>
		<div class="generic-grid-container">
			<grid-view
				v-if="columns"
				:gridData="gridData"
				:loadingData="loading"
				:visibleColumns="gridViewColumns"
				:page="page"
				:rowsPerPage="rowsPerPage"
				:totalCount="totalCount"
				:hasActions="false"
				:sorting="sorting"
				@rowsPerPageChanged="onRowsPerPageChanged"
				@pageChanged="onPageChanged"
			></grid-view>
		</div>
	</div>
</template>

<script>
import FiltersDialog from '../../../../shared/gridViews/FiltersDialog.vue';
import GridView from '../../../../shared/gridViews/GridView.vue';
import QuickFilter from '../../../../shared/gridViews/QuickFilter.vue';
import { mapGetters } from 'vuex';
import { ReportPreviewGetters } from '../../../../../store/getters';
import { ReportPreviewActions } from '../../../../../store/actions';
import { FieldDataTypes } from '@/enums/fieldDataTypes';
import { approvalStatus } from '@/enums/approvalStatus';
import gridViewFieldsUtils from '../../../../../utils/gridViewFieldsUtils';

export default {
	name: 'ReportPreviewRawData',
	props: {
		report: Object,
		workbook: Object,
	},
	components: {
		FiltersDialog,
		QuickFilter,
		GridView,
	},
	computed: {
		...mapGetters({
			columns: [ReportPreviewGetters.REPORT_COLUMNS_RAW],
			rowsPerPage: [ReportPreviewGetters.ROWSPERPAGE],
			totalCount: [ReportPreviewGetters.TOTALCOUNT],
			page: [ReportPreviewGetters.PAGE],
			loading: [ReportPreviewGetters.LOADING],
			gridData: [ReportPreviewGetters.DATA],
			sorting: [ReportPreviewGetters.SORTING],
			quickFilter: [ReportPreviewGetters.QUICKFILTER],
			advancedFilter: [ReportPreviewGetters.ADVANCEDFILTER],
		}),
		filterFields() {
			let result = [];
			if (this.workbook && this.workbook.IsSingleFilter) {
				return this.getWorkbookSingleFilterColumns(
					this.columns,
					this.workbook
				);
			} else {
				this.columns.forEach((c) => {
					result.push({ Id: c.GridViewFieldId, Label: c.Label });
				});
				return result;
			}
		},
		gridViewColumns() {
			let result = [];
			this.columns.forEach((x) => {
				let gridViewColumn = x.GridViewField;
				gridViewColumn.Label = x.Label;
				result.push(gridViewColumn);
			});

			return result;
		},
		FieldDataTypes() {
			return FieldDataTypes;
		},
		ApprovalStatus() {
			return approvalStatus;
		},
		hideFooter() {
			if (this.loading) return true;
			if (this.gridData && this.gridData.length <= 0) return true;
			return false;
		},
		pageStart() {
			return this.rowsPerPage * (this.page - 1);
		},
		pageStop() {
			return this.rowsPerPage * this.page;
		},
		pageCount() {
			if (!this.rowsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(this.totalCount / this.rowsPerPage);
			let remainder = this.totalCount % this.rowsPerPage;
			if (remainder === 0) return quotient;
			return quotient + 1;
		},
		options: {
			get() {
				return {
					page: this.page,
					itemsPerPage: this.rowsPerPage,
				};
			},
			set(data) {
				if (this.page != data.page && !isNaN(data.page))
					this.$store.dispatch(
						ReportPreviewActions.UPDATE_PAGE,
						data.page
					);
				if (
					this.rowsPerPage != data.itemsPerPage &&
					!isNaN(data.itemsPerPage)
				)
					this.$store.dispatch(
						ReportPreviewActions.UPDATE_ROWSPERPAGE,
						data.itemsPerPage
					);
			},
		},
		selectedPage() {
			return this.page;
		},
		selectedRowsPerPage() {
			return this.rowsPerPage;
		},
	},
	mounted() {
		if (this.report) {
			this.$store
				.dispatch(ReportPreviewActions.LOAD_REPORT, {
					report: this.report,
					workbook: null,
				})
				.then(() => {
					this.$store.dispatch(ReportPreviewActions.LOAD_DATA_RAW);
				});
		}
	},

	methods: {
		getFieldName(field) {
			return gridViewFieldsUtils.getFieldName(field);
		},
		showDesc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) => column.FieldId === headerId && column.Operator === 1
			);
		},
		showAsc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) =>
					column.FieldId === headerId && column.Operator === -1
			);
		},
		onQuickFilterChanged(item) {
			this.$store.dispatch(
				ReportPreviewActions.UPDATE_QUICK_FILTER,
				item
			);
		},
		onAdvancedFilterChanged(item) {
			this.$store.dispatch(
				ReportPreviewActions.UPDATE_ADVANCED_FILTER,
				item
			);
		},
		onRowsPerPageChanged(rowsPerPageChanged) {
			this.$store.dispatch(
				ReportPreviewActions.UPDATE_ROWSPERPAGE,
				rowsPerPageChanged
			);
		},
		onPageChanged(page) {
			this.$store.dispatch(ReportPreviewActions.UPDATE_PAGE, page);
		},
		onClose() {
			this.$emit('close');
		},
		getWorkbookSingleFilterColumns(columns, workbook) {
			// Extract all reports from the workbook
			const reports = workbook.WorkbookReports.map((wr) => wr.Report);

			// Create a map to track which columns are used in reports
			const columnUsageMap = {};

			// Initialize the map with all column IDs from the first report
			if (reports.length > 0) {
				reports[0].Fields.forEach((field) => {
					columnUsageMap[field.GridViewFieldId] = {
						count: 1,
						label: field.Label,
						id: field.GridViewFieldId,
					};
				});

				// Count column usage across all other reports
				for (let i = 1; i < reports.length; i++) {
					const reportFields = reports[i].Fields;

					reportFields.forEach((field) => {
						if (columnUsageMap[field.GridViewFieldId]) {
							columnUsageMap[field.GridViewFieldId].count++;
						}
					});
				}
			}

			// Filter columns that appear in all reports
			const commonColumns = Object.values(columnUsageMap)
				.filter((col) => col.count === reports.length)
				.map((col) => ({
					Id: col.id,
					Label: col.label,
				}));
			commonColumns.sort((a, b) => a.Label.localeCompare(b.Label));

			return commonColumns;
		},
	},
};
</script>
